import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { translations, type Language } from '@/lib/translations';

const getInitialLang = (): Language => {
  try {
    const stored = localStorage.getItem('guestLanguage');
    if (stored === 'en' || stored === 'fr') return stored;
  } catch { /* ignore */ }
  return 'en';
};

const CONTENT = {
  en: {
    nav: { login: 'Sign In', signup: 'Get Started Free', beta: 'Public Beta' },
    hero: {
      badge: '🚀 Now in Public Beta',
      title: 'Your money,',
      titleGradient: 'finally under control.',
      subtitle: 'Track income, expenses, and savings effortlessly. Set yearly goals, spot trends, and build better financial habits - all in one beautiful app.',
      cta: 'Start for Free',
      ctaSub: 'No credit card required',
      login: 'Already have an account? Sign in →',
    },
    features: {
      title: 'Everything you need to manage your finances',
      subtitle: 'Simple, powerful, and private - built for real people.',
      items: [
        {
          icon: '📊',
          title: 'Visual Dashboard',
          desc: 'See your income, expenses, and savings at a glance with beautiful charts and monthly summaries.',
        },
        {
          icon: '🔄',
          title: 'Recurring Transactions',
          desc: 'Set up rent, subscriptions, or salary once - MyBalance automatically logs them every month.',
        },
        {
          icon: '🎯',
          title: 'Yearly Savings Goals',
          desc: 'Define a savings target and watch your progress throughout the year with a live progress tracker.',
        },
        {
          icon: '💱',
          title: 'Multi-Currency',
          desc: 'Support for USD, EUR, GBP, JPY, INR, and MGA with live exchange rate conversion.',
        },
        {
          icon: '📥',
          title: 'Export Reports',
          desc: 'Download your full financial history as CSV or Excel - your data, always accessible.',
        },
        {
          icon: '🔒',
          title: 'Bank-Level Security',
          desc: 'Your data is encrypted and strictly private. Not even our team can see your transactions.',
        },
      ],
    },
    security: {
      title: 'Your data is yours - and only yours.',
      subtitle: 'We built MyBalance with security as a first principle, not an afterthought.',
      items: [
        { icon: '🔐', label: 'End-to-end encrypted data storage' },
        { icon: '🛡️', label: 'Strict per-user data isolation rules' },
        { icon: '🚫', label: 'No data selling - ever' },
        { icon: '📋', label: 'Full HTTPS with security headers' },
        { icon: '🔑', label: 'Google OAuth or email sign-in' },
        { icon: '📤', label: 'Export and delete your data anytime' },
      ],
    },
    cta: {
      title: 'Start your financial journey today.',
      subtitle: 'Free during public beta. No credit card. No hidden fees.',
      button: 'Create Free Account',
    },
    footer: {
      rights: '© 2026 Unova. All rights reserved.',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      feedback: 'Send Feedback',
      madeBy: 'Made with ❤️ by',
    },
  },
  fr: {
    nav: { login: 'Se connecter', signup: 'Commencer gratuitement', beta: 'Bêta publique' },
    hero: {
      badge: '🚀 Maintenant en bêta publique',
      title: 'Vos finances,',
      titleGradient: 'enfin maîtrisées.',
      subtitle: 'Suivez vos revenus, dépenses et économies sans effort. Fixez des objectifs, identifiez les tendances et construisez de meilleures habitudes financières.',
      cta: 'Commencer gratuitement',
      ctaSub: 'Aucune carte de crédit requise',
      login: 'Vous avez déjà un compte ? Se connecter →',
    },
    features: {
      title: 'Tout ce dont vous avez besoin pour gérer vos finances',
      subtitle: 'Simple, puissant et privé - conçu pour les vraies personnes.',
      items: [
        {
          icon: '📊',
          title: 'Tableau de bord visuel',
          desc: 'Visualisez vos revenus, dépenses et économies en un coup d\'œil avec de beaux graphiques.',
        },
        {
          icon: '🔄',
          title: 'Transactions récurrentes',
          desc: 'Configurez loyer, abonnements ou salaire une fois - MyBalance les enregistre automatiquement.',
        },
        {
          icon: '🎯',
          title: 'Objectifs d\'épargne annuels',
          desc: 'Définissez un objectif et suivez votre progression tout au long de l\'année.',
        },
        {
          icon: '💱',
          title: 'Multi-devises',
          desc: 'Support USD, EUR, GBP, JPY, INR et MGA avec conversion au taux en temps réel.',
        },
        {
          icon: '📥',
          title: 'Exporter les rapports',
          desc: 'Téléchargez votre historique financier en CSV ou Excel - vos données, toujours accessibles.',
        },
        {
          icon: '🔒',
          title: 'Sécurité bancaire',
          desc: 'Vos données sont chiffrées et strictement privées. Même notre équipe ne peut pas voir vos transactions.',
        },
      ],
    },
    security: {
      title: 'Vos données vous appartiennent - et uniquement à vous.',
      subtitle: 'Nous avons construit MyBalance avec la sécurité comme premier principe.',
      items: [
        { icon: '🔐', label: 'Stockage de données chiffré' },
        { icon: '🛡️', label: 'Isolation stricte des données par utilisateur' },
        { icon: '🚫', label: 'Aucune vente de données - jamais' },
        { icon: '📋', label: 'HTTPS complet avec en-têtes de sécurité' },
        { icon: '🔑', label: 'Connexion Google OAuth ou e-mail' },
        { icon: '📤', label: 'Exportez et supprimez vos données à tout moment' },
      ],
    },
    cta: {
      title: 'Commencez votre parcours financier aujourd\'hui.',
      subtitle: 'Gratuit pendant la bêta publique. Pas de carte de crédit. Pas de frais cachés.',
      button: 'Créer un compte gratuit',
    },
    footer: {
      rights: '© 2026 Unova. Tous droits réservés.',
      privacy: 'Politique de confidentialité',
      terms: 'Conditions d\'utilisation',
      feedback: 'Envoyer des commentaires',
      madeBy: 'Fait avec ❤️ par',
    },
  },
};

export function Landing() {
  const [lang, setLang] = useState<Language>(getInitialLang);
  const navigate = useNavigate();
  const c = CONTENT[lang];

  const handleLanguageSwitch = (newLang: Language) => {
    setLang(newLang);
    try { localStorage.setItem('guestLanguage', newLang); } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      {/* ── Navbar ── */}
      <nav className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 backdrop-blur-xl dark:border-slate-700 dark:bg-slate-900/95">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 shadow-md">
              <span className="text-xl">💰</span>
            </div>
            <span className="text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              MyBalance
            </span>
            <span className="hidden sm:inline ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
              {c.nav.beta}
            </span>
          </div>

          {/* Right nav */}
          <div className="flex items-center gap-2">
            {/* Language switcher */}
            <div className="flex items-center gap-1 rounded-lg bg-slate-100 dark:bg-slate-800 p-1 mr-1">
              {(['en', 'fr'] as Language[]).map((l) => (
                <button
                  key={l}
                  onClick={() => handleLanguageSwitch(l)}
                  className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${
                    lang === l
                      ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
                  }`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            <button
              onClick={() => navigate('/login')}
              className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-700 hover:text-blue-600 dark:text-slate-300 dark:hover:text-blue-400 transition-colors"
            >
              {c.nav.login}
            </button>
            <button
              onClick={() => navigate('/signup')}
              className="rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-2 text-sm font-semibold text-white shadow-lg hover:shadow-xl hover:opacity-90 transition-all active:scale-95"
            >
              {c.nav.signup}
            </button>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-slate-900 dark:via-purple-900/20 dark:to-slate-900">
        {/* Background blobs */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-purple-400/20 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-blue-400/20 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28 lg:px-8 lg:py-36">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mb-6 inline-flex items-center rounded-full border border-blue-200 bg-white/80 px-4 py-1.5 text-sm font-semibold text-blue-700 shadow-sm backdrop-blur-sm dark:border-blue-800 dark:bg-slate-800/80 dark:text-blue-400">
              {c.hero.badge}
            </div>
            <h1 className="mb-6 text-5xl font-black leading-tight tracking-tight text-slate-900 dark:text-white sm:text-6xl lg:text-7xl">
              {c.hero.title}{' '}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                {c.hero.titleGradient}
              </span>
            </h1>
            <p className="mb-10 text-lg leading-relaxed text-slate-600 dark:text-slate-400 sm:text-xl">
              {c.hero.subtitle}
            </p>
            <div className="flex flex-col items-center gap-3">
              <button
                onClick={() => navigate('/signup')}
                className="min-h-[54px] w-full max-w-xs rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-4 text-base font-bold text-white shadow-xl hover:shadow-2xl hover:opacity-90 transition-all active:scale-95 sm:w-auto"
              >
                {c.hero.cta} →
              </button>
              <p className="text-sm text-slate-500 dark:text-slate-400">{c.hero.ctaSub}</p>
              <button
                onClick={() => navigate('/login')}
                className="text-sm font-medium text-blue-600 hover:text-purple-600 dark:text-blue-400 dark:hover:text-purple-400 transition-colors"
              >
                {c.hero.login}
              </button>
            </div>
          </div>

          {/* Mock Dashboard Preview */}
          <div className="mt-16 mx-auto max-w-4xl">
            <div className="rounded-2xl border border-slate-200 bg-white/80 p-6 shadow-2xl backdrop-blur-xl dark:border-slate-700 dark:bg-slate-800/80 sm:rounded-3xl sm:p-8">
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Year 2026</p>
                  <p className="text-2xl font-black text-slate-900 dark:text-white sm:text-3xl">📊 Dashboard</p>
                </div>
                <div className="flex gap-2">
                  <div className="h-3 w-3 rounded-full bg-red-400" />
                  <div className="h-3 w-3 rounded-full bg-yellow-400" />
                  <div className="h-3 w-3 rounded-full bg-green-400" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 sm:gap-4">
                <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 p-3 dark:from-emerald-900/20 dark:to-teal-900/20 sm:p-4">
                  <p className="text-xs text-slate-500 dark:text-slate-400">Total Savings</p>
                  <p className="text-lg font-black text-emerald-600 sm:text-2xl">$4,820</p>
                  <p className="text-xs text-emerald-500 font-medium">↑ 12%</p>
                </div>
                <div className="rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 p-3 dark:from-blue-900/20 dark:to-indigo-900/20 sm:p-4">
                  <p className="text-xs text-slate-500 dark:text-slate-400">Total Income</p>
                  <p className="text-lg font-black text-blue-600 sm:text-2xl">$18,400</p>
                  <p className="text-xs text-blue-500 font-medium">↑ 5%</p>
                </div>
                <div className="rounded-xl bg-gradient-to-br from-red-50 to-pink-50 p-3 dark:from-red-900/20 dark:to-pink-900/20 sm:p-4">
                  <p className="text-xs text-slate-500 dark:text-slate-400">Total Expenses</p>
                  <p className="text-lg font-black text-red-500 sm:text-2xl">$13,580</p>
                  <p className="text-xs text-red-400 font-medium">↓ 3%</p>
                </div>
              </div>
              {/* Fake savings bar */}
              <div className="mt-4 rounded-xl bg-slate-50 p-4 dark:bg-slate-700/40">
                <div className="mb-2 flex justify-between text-xs font-semibold text-slate-600 dark:text-slate-400">
                  <span>🎯 Yearly Goal Progress</span>
                  <span>$4,820 / $10,000</span>
                </div>
                <div className="h-3 w-full rounded-full bg-slate-200 dark:bg-slate-600">
                  <div className="h-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-500" style={{ width: '48%' }} />
                </div>
                <p className="mt-1 text-right text-xs text-slate-500">48% achieved</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-3xl font-black text-slate-900 dark:text-white sm:text-4xl lg:text-5xl">
              {c.features.title}
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400">{c.features.subtitle}</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {c.features.items.map((feature) => (
              <div
                key={feature.title}
                className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:border-blue-200 hover:shadow-lg dark:border-slate-700 dark:bg-slate-800 dark:hover:border-blue-800"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-50 to-purple-50 text-2xl dark:from-blue-900/20 dark:to-purple-900/20">
                  {feature.icon}
                </div>
                <h3 className="mb-2 text-lg font-bold text-slate-900 dark:text-white">{feature.title}</h3>
                <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Security Section ── */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-black text-white sm:text-4xl lg:text-5xl">
              {c.security.title}
            </h2>
            <p className="text-lg text-slate-400">{c.security.subtitle}</p>
          </div>
          <div className="mx-auto grid max-w-4xl gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {c.security.items.map((item) => (
              <div
                key={item.label}
                className="flex items-center gap-3 rounded-xl border border-slate-700 bg-slate-800/60 p-4 backdrop-blur-sm"
              >
                <span className="text-2xl flex-shrink-0">{item.icon}</span>
                <p className="text-sm font-medium text-slate-300">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl rounded-3xl bg-gradient-to-br from-blue-600 to-purple-600 p-10 text-center shadow-2xl sm:p-16">
            <h2 className="mb-4 text-3xl font-black text-white sm:text-4xl">{c.cta.title}</h2>
            <p className="mb-8 text-lg text-blue-100">{c.cta.subtitle}</p>
            <button
              onClick={() => navigate('/signup')}
              className="min-h-[54px] rounded-2xl bg-white px-10 py-4 text-base font-bold text-blue-600 shadow-xl hover:bg-blue-50 transition-all active:scale-95"
            >
              {c.cta.button} →
            </button>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-slate-200 bg-white py-10 dark:border-slate-700 dark:bg-slate-900">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-purple-600">
                <span className="text-sm">💰</span>
              </div>
              <span className="text-sm font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                MyBalance
              </span>
            </div>
            <div className="flex flex-wrap justify-center gap-4 text-xs text-slate-500 dark:text-slate-400">
              <button
                onClick={() => navigate('/privacy')}
                className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                {c.footer.privacy}
              </button>
              <button
                onClick={() => navigate('/terms')}
                className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                {c.footer.terms}
              </button>
              <a
                href="mailto:contact@mybalancya.com"
                className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                {c.footer.feedback}
              </a>
            </div>
            <p className="text-xs text-slate-400 dark:text-slate-500">
              {c.footer.madeBy}{' '}
              <span className="font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Unova
              </span>
              {' '}· {c.footer.rights}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
